import React, { useCallback } from 'react';
import { Icon, TextField, Spinner, Checkbox, Button, Accordion } from '@scale/scaleui';
import _ from 'lodash';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';

import {
  SearchResultV2,
  SearchResultAction as ISearchResultAction,
  SearchResultActionType,
} from '@scale/shared/types/chat';
import { AsyncState } from 'react-use-v13/lib/useAsyncFn';
import SearchResultAction from './SearchResultAction';
import { validateAction, DEFAULT_SEARCH_ACTION } from '../utils';

interface SearchResultsSectionProps {
  searchQuery: string;
  setSearchQuery: (searchQuery: string) => void;
  searchJustification: string;
  setSearchQueryJustification?: (searchQuery: string) => void;
  readOnly: boolean;
  searchQueryResults: SearchResultV2[];
  disableFetchSearchResults: boolean;
  searchState: AsyncState<void>;
  fetchSearchResults: (searchTerm?: string, justification?: string) => Promise<void>;
  onCheckboxChange: (index: number) => void;
  resultIndex: number;
  handleAddSearchResultAction: (index: number, action: ISearchResultAction) => void;
  handleEditSearchResultAction: (
    index: number,
    actionIndex: number,
    action: ISearchResultAction,
  ) => void;
  handleRemoveSearchResultAction: (index: number, actionIndex: number) => void;
  handleActionReorder: (index: number, sourceIndex: number, destinationIndex: number) => void;
}

const SearchResultsSection: React.FC<SearchResultsSectionProps> = ({
  searchQuery,
  setSearchQuery,
  searchJustification,
  setSearchQueryJustification,
  readOnly,
  searchQueryResults,
  disableFetchSearchResults,
  searchState,
  fetchSearchResults,
  onCheckboxChange,
  resultIndex,
  handleAddSearchResultAction,
  handleEditSearchResultAction,
  handleRemoveSearchResultAction,
  handleActionReorder,
}) => {
  const handleClickReferenceLink = useCallback(
    (index: number, url: string) => {
      handleAddSearchResultAction(index, {
        actionType: SearchResultActionType.Click,
        currentUrl: url,
        thoughts: '',
        input: '',
        output: '',
      });
    },
    [handleAddSearchResultAction],
  );

  return (
    <>
      <div className="flex flex-row items-center justify-start gap-1 w-full">
        <div className="w-full">
          <TextField
            label="Search Query"
            id="text-field"
            className="flex-1"
            disabled={readOnly}
            onChange={e => {
              setSearchQuery(e.target.value);
            }}
            placeholder="Enter a search query"
            type="text"
            value={searchQuery}
          />
          {setSearchQueryJustification !== undefined && (
            <TextField
              label="Justification"
              labelTooltip="Justification for the query. Explain why you are making this query."
              id="text-field-2"
              className="flex-1"
              disabled={readOnly}
              onChange={e => {
                setSearchQueryJustification(e.target.value);
              }}
              placeholder="Enter the justification for the query"
              type="text"
              value={searchJustification}
            />
          )}
        </div>
        <div>
          {searchState.loading && (
            <div className="w-10 h-10 flex items-center justify-center">
              <Spinner size="sm" variant="primary" />
            </div>
          )}
          {!searchState.loading && (
            <Icon
              className="mt-6"
              icon={['far', 'search']}
              size="lg"
              variant="neutral"
              disabled={
                disableFetchSearchResults ||
                searchQuery.length === 0 ||
                searchJustification.length === 0
              }
              onClick={async () => {
                await fetchSearchResults(searchQuery, searchJustification);
              }}
            />
          )}
        </div>
      </div>
      <div className="flex-1">
        {searchState.error && (
          <div className="flex justify-center items-center text-danger-400">
            <Icon variant="danger" size="sm" icon="exclamation-circle" />
            Error fetching search results. Please try again after a few seconds.
          </div>
        )}
        {!searchState.loading && !searchState.error && (
          <div className="flex flex-col gap-4">
            {searchQueryResults.map((result, index) => {
              const searchResult = searchQueryResults[index];
              const actions = searchResult.actions ?? [];
              const actionsValidationResults = actions.map(validateAction);
              const allActionsValid = _.every(actionsValidationResults, v => v.isValid);
              const warning = (() => {
                if (!allActionsValid) {
                  return 'Please make sure all actions are valid';
                } else if (searchResult.isChecked && actions.length === 0) {
                  return 'At least one action is required';
                }
              })();
              return (
                <div key={index} className="border border-neutral-100 rounded-md p-2">
                  <div className="flex flex-row items-center gap-2">
                    <Checkbox
                      checked={searchResult.isChecked}
                      disabled={readOnly}
                      id={`checkbox-${index}`}
                      onChange={() => {
                        onCheckboxChange(index);
                      }}
                    />
                    <a
                      className="text-info-600 no-underline hover:underline break-all"
                      href={result.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => handleClickReferenceLink(index, result.url ?? '')}
                    >
                      {`[${resultIndex}.${index + 1}] ${
                        result.title ?? result.sourceTitle ?? result.link ?? result.url ?? ''
                      }`}
                    </a>
                  </div>
                  <div className="text-sm text-neutral-700 break-words">{result.snippet}</div>
                  {searchResult.isChecked && (
                    <Accordion
                      header={
                        <div className="flex items-center gap-2">
                          <span>Actions ({actions.length})</span>{' '}
                          {warning && (
                            <div className="text-danger-600 flex items-center">
                              <span>
                                <Icon icon="exclamation-circle" variant="danger" />
                              </span>
                              {warning}
                            </div>
                          )}
                        </div>
                      }
                      target={
                        <div>
                          <div>
                            <DragDropContext
                              onDragEnd={(dropResult: DropResult) => {
                                if (!dropResult.destination) return;
                                handleActionReorder(
                                  index,
                                  dropResult.source.index,
                                  dropResult.destination.index,
                                );
                              }}
                            >
                              <Droppable droppableId={`droppable-${index}`}>
                                {provided => (
                                  <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                    className="flex flex-col gap-1"
                                  >
                                    {actions.map((action, actionIndex) => (
                                      <Draggable
                                        key={actionIndex}
                                        draggableId={`lint-rule-${index}-${actionIndex}`}
                                        index={actionIndex}
                                      >
                                        {provided => (
                                          <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            className="flex items-center w-full"
                                            style={{
                                              ...provided.draggableProps.style,
                                            }}
                                          >
                                            <div
                                              {...provided.dragHandleProps}
                                              style={{ cursor: 'grab' }}
                                            >
                                              <Icon
                                                variant="neutral"
                                                size="md"
                                                icon="grip-vertical"
                                              />
                                            </div>
                                            <div className="flex-1">
                                              <SearchResultAction
                                                action={action}
                                                actionIndex={actionIndex}
                                                onRemove={() =>
                                                  handleRemoveSearchResultAction(index, actionIndex)
                                                }
                                                onEdit={newAction =>
                                                  handleEditSearchResultAction(
                                                    index,
                                                    actionIndex,
                                                    newAction,
                                                  )
                                                }
                                                validationResult={
                                                  actionsValidationResults[actionIndex]
                                                }
                                              />
                                            </div>
                                          </div>
                                        )}
                                      </Draggable>
                                    ))}
                                    {provided.placeholder}
                                  </div>
                                )}
                              </Droppable>
                            </DragDropContext>
                          </div>
                          <Button
                            className="mt-2"
                            variant="secondary"
                            onClick={() => {
                              handleAddSearchResultAction(index, DEFAULT_SEARCH_ACTION);
                            }}
                            disabled={!allActionsValid}
                          >
                            New Action
                          </Button>
                        </div>
                      }
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default SearchResultsSection;
